import { useEffect, useMemo, useState, useCallback } from "react";
import {
  Alert,
  Button,
  Card,
  Input,
  Select,
  Spin,
  Typography,
  message,
  Modal,
  Result,
} from "antd";
import {
  ReloadOutlined,
  BankOutlined,
  DatabaseOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import {
  getInstitutionManageAll,
  getInstitutionHead,
  activateInstitution,
  deactivateInstitution,
} from "../api/institutionApi";
import { syncBankBalances } from "../api/bankAccountApi";
import { hasAnyRole } from "../api/auth";
import TradingHouseList from "../components/institutions/TradingHouseList";
import { useOpenRoute } from "../utils/openRouteUtils";

const { Title, Text } = Typography;

const HEAD_INSTITUTION_ID = 14;
const FILTER_STORAGE_KEY = "head-institution-control:trading-house-filter";

export default function HeadInstitutionControllPage() {
  const openRoute = useOpenRoute();
  const isAdmin = hasAnyRole(["ADMIN"]);
  const isModerator = hasAnyRole(["MODERATOR"]);
  const isAuthor = hasAnyRole(["AUTHOR"]);

  const canViewPage = isAdmin || isModerator || isAuthor;
  const canManage = isAdmin || isModerator;
  const canSeeHeadInstitution = isAdmin;

  const [data, setData] = useState([]);
  const [headInstitution, setHeadInstitution] = useState(null);
  const [loading, setLoading] = useState(canViewPage);
  const [error, setError] = useState("");
  const [togglingInstitutionId, setTogglingInstitutionId] = useState(null);
  const [syncingBalances, setSyncingBalances] = useState(false);

  const [verifyModal, setVerifyModal] = useState({
    open: false,
    action: null,
    item: null,
    input: "",
  });

  const [searchText, setSearchText] = useState(() => {
    return localStorage.getItem(`${FILTER_STORAGE_KEY}:search`) || "";
  });

  const [selectedInstitutionId, setSelectedInstitutionId] = useState(() => {
    return localStorage.getItem(`${FILTER_STORAGE_KEY}:institutionId`) || "all";
  });

  const closeVerifyModal = () => {
    setVerifyModal({
      open: false,
      action: null,
      item: null,
      input: "",
    });
  };

  const loadData = useCallback(async () => {
    if (!canViewPage) return;

    try {
      setLoading(true);
      setError("");

      const [allResponse, headResponse] = await Promise.all([
        getInstitutionManageAll(),
        canSeeHeadInstitution ? getInstitutionHead() : Promise.resolve(null),
      ]);
      setData(Array.isArray(allResponse) ? allResponse : []);
      setHeadInstitution(headResponse || null);
    } catch (err) {
      console.error(err);
      setError(err?.message || "Байгууллагын мэдээллийг ачаалж чадсангүй");
      setData([]);
      setHeadInstitution(null);
    } finally {
      setLoading(false);
    }
  }, [canViewPage, canSeeHeadInstitution]);

  useEffect(() => {
    if (canViewPage) {
      loadData();
    }
  }, [canViewPage, loadData]);

  useEffect(() => {
    localStorage.setItem(`${FILTER_STORAGE_KEY}:search`, searchText);
  }, [searchText]);

  useEffect(() => {
    localStorage.setItem(
      `${FILTER_STORAGE_KEY}:institutionId`,
      selectedInstitutionId
    );
  }, [selectedInstitutionId]);

  const visibleInstitutions = useMemo(() => {
    return data.filter((item) => item?.institution?.id !== HEAD_INSTITUTION_ID);
  }, [data]);

  const allTradingHouses = useMemo(() => {
    return visibleInstitutions.filter(
      (item) => item?.institution?.type === "TRADING_HOUSE"
    );
  }, [visibleInstitutions]);

  const tradingHouseOptions = useMemo(() => {
    return allTradingHouses
      .map((item) => ({
        value: String(item?.institution?.id),
        label:
          item?.institution?.name || `Institution ${item?.institution?.id ?? ""}`,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [allTradingHouses]);

  const filteredTradingHouses = useMemo(() => {
    const normalizedSearch = searchText.trim().toLowerCase();

    return allTradingHouses.filter((item) => {
      const institutionId = String(item?.institution?.id ?? "");
      const institutionName = item?.institution?.name?.toLowerCase() || "";

      const matchesSelect =
        selectedInstitutionId === "all" ||
        institutionId === selectedInstitutionId;

      const matchesSearch =
        !normalizedSearch ||
        institutionName.includes(normalizedSearch) ||
        institutionId.includes(normalizedSearch);

      return matchesSelect && matchesSearch;
    });
  }, [allTradingHouses, searchText, selectedInstitutionId]);

  const activeTradingHouses = useMemo(() => {
    return filteredTradingHouses.filter((item) =>
      Boolean(item?.institution?.is_active)
    );
  }, [filteredTradingHouses]);

  const hiddenTradingHouses = useMemo(() => {
    return filteredTradingHouses.filter(
      (item) => !Boolean(item?.institution?.is_active)
    );
  }, [filteredTradingHouses]);

  const handleOpenAccounts = (item, event) => {
    const institutionId = item?.institution?.id;
    if (!institutionId) return;

    openRoute(`/institutions/${institutionId}/accounts`, event);
  };

  const handleOpenHeadInstitutionAccounts = (item, event) => {
    if (!canSeeHeadInstitution) return;

    const institutionId = item?.institution?.id;
    if (!institutionId) return;

    openRoute(`/institutions/${institutionId}/accounts`, event);
  };

  const handleOpenWallets = (item, event) => {
    const institutionId = item?.institution?.id;
    if (!institutionId) return;

    openRoute(`/institutions/${institutionId}/wallets`, event);
  };

  const handleResetFilters = () => {
    setSearchText("");
    setSelectedInstitutionId("all");
  };

  const handleTempSyncBalances = async () => {
    if (!canManage) {
      message.error("Танд энэ үйлдлийг хийх эрх алга");
      return;
    }

    try {
      setSyncingBalances(true);
      await syncBankBalances();
      message.success("Банкны дансны үлдэгдэл амжилттай шинэчлэгдлээ");
    } catch (err) {
      console.error(err);
      message.error("Банкны дансны үлдэгдлийг шинэчлэхэд алдаа гарлаа");
    } finally {
      setSyncingBalances(false);
    }
  };

  const handleToggleInstitution = (item, checked) => {
    if (!canManage) {
      message.error("Танд энэ үйлдлийг хийх эрх алга");
      return;
    }

    const institutionId = item?.institution?.id;
    if (!institutionId) return;

    setVerifyModal({
      open: true,
      action: checked ? "activate" : "deactivate",
      item,
      input: "",
    });
  };

  const handleConfirmToggle = async () => {
    if (!canManage) {
      message.error("Танд энэ үйлдлийг хийх эрх алга");
      return;
    }

    const { item, action, input } = verifyModal;
    const institutionId = item?.institution?.id;

    if (!institutionId || !action) return;

    const expectedText = action === "activate" ? "A" : "D";
    const normalizedInput = String(input || "").trim().toUpperCase();

    if (normalizedInput !== expectedText) {
      message.error(`Type "${expectedText}" to confirm`);
      return;
    }

    try {
      setTogglingInstitutionId(institutionId);

      if (action === "activate") {
        await activateInstitution(institutionId);
        message.success("Байгууллага амжилттай идэвхжлээ");
      } else {
        await deactivateInstitution(institutionId);
        message.success("Байгууллага идэвхгүй боллоо");
      }

      await loadData();
      closeVerifyModal();
    } catch (err) {
      console.error(err);
      message.error(err?.message || "Байгууллагын төлөв шинэчлэхэд алдаа гарлаа");
    } finally {
      setTogglingInstitutionId(null);
    }
  };

  if (!canViewPage) {
    return (
      <div className="min-h-screen bg-slate-100 px-6 py-8">
        <div className="mx-auto max-w-4xl">
          <Card className="rounded-2xl border-0 shadow-sm">
            <Result
              status="403"
              title="403"
              subTitle="Энэ хуудсанд хандах эрх алга"
            />
          </Card>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex min-h-[320px] items-center justify-center">
        <Spin size="large" />
      </div>
    );
  }

  const options = [
    { value: "all", label: "Бүх трейдинг хаус" },
    ...tradingHouseOptions.map((option) => ({
      value: option.value,
      label: option.label,
    })),
  ];

  return (
    <div className="min-h-screen bg-slate-50 px-6 py-8">
      <div className="mx-auto">
        <div className="mb-8 flex items-start justify-between gap-4">
          {canManage ? (
            <div className="mb-4 items-center">
              <Button
                type="primary"
                loading={syncingBalances}
                onClick={handleTempSyncBalances}
              >
                Үлдэгдэл синк хийх (temp)
              </Button>
            </div>
          ) : null}

          <Button icon={<ReloadOutlined />} onClick={loadData}>
            Шинэчлэх
          </Button>
        </div>

        {error ? (
          <Card className="mb-6 rounded-2xl">
            <Alert
              type="error"
              message="Ачааллахад алдаа гарлаа"
              description={error}
              showIcon
            />
          </Card>
        ) : null}

        {canSeeHeadInstitution && headInstitution ? (
          <Card className="mb-8 rounded-[28px] border-0 bg-white shadow-sm">
            <div className="flex flex-col gap-5 px-6 py-5 md:flex-row md:items-center md:justify-between md:px-7">
              <div className="flex min-w-0 items-center gap-4">
                <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-slate-100 text-slate-700">
                  <BankOutlined className="text-[22px]" />
                </div>

                <div className="min-w-0">
                  <div className="mb-1 text-[15px] font-medium uppercase tracking-[0.28em] text-slate-500">
                    Банк бус
                  </div>

                  <div className="truncate text-[28px] font-semibold leading-tight text-slate-900">
                    {headInstitution?.institution?.name ||
                      `Institution ${HEAD_INSTITUTION_ID}`}
                  </div>
                  <span className="text-md text-slate-500">
                    ID {HEAD_INSTITUTION_ID ?? "-"}
                  </span>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <Button
                  icon={<DatabaseOutlined />}
                  className="h-10 rounded-xl border-slate-200 px-4"
                  onClick={(e) =>
                    handleOpenHeadInstitutionAccounts(headInstitution, e)
                  }
                >
                  Данснууд
                </Button>
              </div>
            </div>
          </Card>
        ) : null}

        <div className="mb-4 flex flex-col gap-4 md:flex-row md:items-end md:justify-end">
          <div className="flex w-full flex-col gap-3 md:w-auto md:flex-row">
            <Input
              allowClear
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder="Трейдинг хаус хайх"
              prefix={<SearchOutlined className="text-slate-400" />}
              className="h-11 min-w-[260px] rounded-xl"
            />

            <Select
              value={selectedInstitutionId}
              onChange={setSelectedInstitutionId}
              className="min-w-[260px]"
              size="large"
              options={options}
              showSearch
              optionFilterProp="label"
            />

            <Button
              className="h-11 rounded-xl border-slate-200 px-4"
              onClick={handleResetFilters}
            >
              Цэвэрлэх
            </Button>
          </div>
        </div>

        <div className="space-y-10">
          <div>
            <div className="mb-3 flex items-center justify-between">
              <Title level={5} className="!mb-0">
                Идэвхтэй трейдинг хаусууд
              </Title>
              <Text className="text-slate-500">
                {activeTradingHouses.length}
              </Text>
            </div>

            <TradingHouseList
              items={activeTradingHouses}
              onAccounts={handleOpenAccounts}
              onWallets={handleOpenWallets}
              onToggleActive={handleToggleInstitution}
              togglingInstitutionId={togglingInstitutionId}
              canManage={canManage}
              emptyText="Идэвхтэй трейдинг хаус олдсонгүй"
            />
          </div>

          <div>
            <div className="mb-3 flex items-center justify-between">
              <Title level={5} className="!mb-0 text-slate-600">
                Идэвхгүй трейдинг хаусууд
              </Title>
              <Text className="text-slate-500">
                {hiddenTradingHouses.length}
              </Text>
            </div>

            <TradingHouseList
              items={hiddenTradingHouses}
              onAccounts={handleOpenAccounts}
              onWallets={handleOpenWallets}
              onToggleActive={handleToggleInstitution}
              togglingInstitutionId={togglingInstitutionId}
              canManage={canManage}
              emptyText="Идэвхгүй трейдинг хаус олдсонгүй"
            />
          </div>
        </div>
      </div>

      <Modal
        open={verifyModal.open}
        onCancel={closeVerifyModal}
        onOk={handleConfirmToggle}
        okButtonProps={{
          disabled:
            String(verifyModal.input || "").trim().toUpperCase() !==
            (verifyModal.action === "activate" ? "A" : "D"),
          loading: togglingInstitutionId != null,
        }}
        cancelButtonProps={{
          disabled: togglingInstitutionId != null,
        }}
        title={
          verifyModal.action === "activate"
            ? "Идэвхжүүлэхийг баталгаажуулах"
            : "Идэвхгүй болгохыг баталгаажуулах"
        }
      >
        <div className="flex flex-col gap-3">
          <div className="text-sm text-slate-600">
            {verifyModal.action === "activate"
              ? "Энэ үйлдэл байгууллагыг идэвхжүүлнэ."
              : "Энэ үйлдэл байгууллагыг идэвхгүй болгоно. Энэ нь гүйлгээ болон тайланд нөлөөлж болзошгүй."}
          </div>

          <div className="text-sm font-semibold text-red-600">
            Баталгаажуулахын тулд{" "}
            <span className="font-mono">
              {verifyModal.action === "activate" ? "A" : "D"}
            </span>{" "}
            гэж бичнэ үү.
          </div>

          <Input
            value={verifyModal.input}
            onChange={(e) =>
              setVerifyModal((prev) => ({
                ...prev,
                input: e.target.value,
              }))
            }
            placeholder={verifyModal.action === "activate" ? "A" : "D"}
            onPressEnter={handleConfirmToggle}
            disabled={togglingInstitutionId != null}
          />
        </div>
      </Modal>
    </div>
  );
}